import { useRef, useState } from 'react';

import { PAGE_SIZE } from '@/lib/datocms/constants';
import { Button, P3 } from '@bumble/ui-kit';
import { useRouter } from 'next/router';

import styles from './pagination.module.scss';

// eslint-disable-next-line no-restricted-syntax
function* paginationGenerator(pageSize: number = PAGE_SIZE) {
  let skip = pageSize;

  while (true) {
    // eslint-disable-next-line no-restricted-syntax
    yield { first: pageSize, skip };
    skip += pageSize;
  }
}

interface Props {
  setStories: (value: any) => void;
  hasMoreStories: boolean;
  setHasMoreStories: (value: boolean) => void;
}

export default function Pagination({ setStories, hasMoreStories, setHasMoreStories }: Props) {
  const paginationGen = useRef(paginationGenerator(PAGE_SIZE));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  async function getMoreStories() {
    const { first, skip } = paginationGen.current.next().value as {
      first: number;
      skip: number;
    };

    try {
      setIsLoading(true);
      const currentTag = new URLSearchParams(window.location.search).get('tag');

      const response = await fetch(
        `/api/stories?first=${first + 1}&locale=${router.locale}&skip=${skip}${
          currentTag ? `&tag=${currentTag}` : ''
        }`,
      );
      const json = await response.json();

      const fetchedStories = json.allStories || [];

      if (fetchedStories.length > 0) {
        const storiesToAdd = fetchedStories.slice(0, first);

        setStories((prevStories: any[]) => [...prevStories, ...storiesToAdd]);
      }

      if (fetchedStories.length <= first) {
        setHasMoreStories(false);
      }

      return json;
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error) {
      console.error(error);
      setError('Failed to load more stories.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.pagination}>
      {hasMoreStories && (
        <Button
          onClick={getMoreStories}
          isDisabled={isLoading || !!error}
          text={isLoading ? 'Loading...' : 'Load More Stories'}
          labelText="View more stories"
          designType="secondary"
          size="small"
          icon="generic-plus"
          iconPosition="end"
        />
      )}
      {error && <P3>{error}</P3>}
    </div>
  );
}
